// One condition row of the segment builder (UI-4): what it is about, how it
// compares, and the value — each editor shaped for its kind of field, the
// way Klaviyo's rows read ("Kattintott levélben · az elmúlt N napban · 30 nap").

import { useId, useState } from "react";
import type { ClipboardEvent, KeyboardEvent } from "react";
import { InfoIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";
import type { BuilderEnv, PickerOption } from "@/pages/segments/data";
import {
  ATTR,
  BETWEEN_DATES,
  CATEGORIES,
  CONSENT_WORDS,
  LISTED,
  MAX_DAYS,
  VALUELESS,
  attributeKeyOk,
  conditionFor,
  notesFor,
  offeredSubjects,
  operatorsFor,
  subjectInfo,
} from "@/pages/segments/model";
import type { Catalogue, ConditionDraft, FieldInfo } from "@/pages/segments/model";

interface Props {
  condition: ConditionDraft;
  catalogue: Catalogue;
  env: BuilderEnv;
  /** The row's problem, if it has one. */
  error: string | null;
  /** After a save attempt: the problem is an error, not a hint. */
  strict?: boolean;
  onChange: (next: ConditionDraft) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export function ConditionEditor({ condition, catalogue, env, error, strict, onChange, onRemove, disabled }: Props) {
  const info = catalogue.get(condition.subject);
  const operators = info ? operatorsFor(condition.subject, catalogue) : [];
  const subjects = offeredSubjects(catalogue);
  const errorId = useId();

  function pick(next: string) {
    onChange({ ...conditionFor(next, catalogue), id: condition.id });
  }

  function operator(op: string) {
    // The value stays where it still means something: a day count survives
    // "az elmúlt N napban" ↔ "…nem", a list "egyike" ↔ "egyik sem", a date
    // "után" ↔ "előtt".
    if (shapeOf(op) === shapeOf(condition.op)) {
      onChange({ ...condition, op });
      return;
    }
    onChange({ ...condition, op, text: shapeOf(op) === "days" ? "30" : "", list: [], low: "", high: "" });
  }

  const attributeWarning = condition.subject === ATTR ? attributeNote(env) : null;
  const notes = attributeWarning ? [attributeWarning] : notesFor(condition.subject, condition.op);

  return (
    <div data-slot="condition" className="flex min-w-0 flex-col gap-1.5">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <NativeSelect
          aria-label="Mire szűrjön"
          className="w-52"
          value={condition.subject}
          disabled={disabled}
          onChange={(event) => pick(event.target.value)}
        >
          <option value="">Válassz feltételt…</option>
          {CATEGORIES.map((category) => {
            const inside = subjects.filter((candidate) => candidate.category === category.key);
            if (inside.length === 0) return null;
            return (
              <optgroup key={category.key} label={category.label}>
                {inside.map((candidate) => (
                  <option key={candidate.field} value={candidate.field}>
                    {candidate.label}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </NativeSelect>

        {condition.subject === ATTR && (
          <AttributeKey
            value={condition.attrKey}
            env={env}
            disabled={disabled}
            onChange={(attrKey) => onChange({ ...condition, attrKey, text: "", list: [] })}
          />
        )}

        {info && (
          <NativeSelect
            aria-label="Művelet"
            className="w-44"
            value={condition.op}
            disabled={disabled}
            onChange={(event) => operator(event.target.value)}
          >
            {!operators.some((choice) => choice.op === condition.op) && <option value="">Művelet…</option>}
            {operators.map((choice) => (
              <option key={choice.op} value={choice.op}>
                {choice.label}
              </option>
            ))}
          </NativeSelect>
        )}

        {info && condition.op && !VALUELESS.has(condition.op) && (
          <ValueEditor condition={condition} info={info} env={env} disabled={disabled} onChange={onChange} />
        )}

        <Button
          variant="ghost"
          size="icon-sm"
          type="button"
          className="ml-auto"
          aria-label="Feltétel törlése"
          disabled={disabled}
          onClick={onRemove}
        >
          <XIcon />
        </Button>
      </div>

      {notes.map((note) => (
        <p
          key={note.text}
          className={cn("flex items-start gap-1.5 text-label", note.warn ? "text-warn" : "text-muted-foreground")}
        >
          {note.warn ? (
            <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" />
          ) : (
            <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
          )}
          <span>{note.text}</span>
        </p>
      ))}
      {error && (
        <p
          id={errorId}
          role={strict ? "alert" : undefined}
          className={cn("text-label", strict ? "text-danger" : "text-muted-foreground")}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/** What kind of value an operator takes, so switching keeps what still fits. */
function shapeOf(op: string): "none" | "days" | "list" | "range" | "one" {
  if (VALUELESS.has(op)) return "none";
  if (op === "within_days" || op === "not_within_days") return "days";
  if (LISTED.has(op)) return "list";
  if (op === "between" || op === BETWEEN_DATES) return "range";
  return "one";
}

function attributeNote(env: BuilderEnv): { text: string; warn: boolean } | null {
  const refused = env.attributes?.keys.filter((key) => !key.filterable) ?? [];
  if (refused.length === 0) return null;
  const names = refused.slice(0, 4).map((key) => `„${key.key}”`);
  const more = refused.length > 4 ? ` és még ${refused.length - 4}` : "";
  return {
    warn: true,
    text: `A Klaviyo-importból ${refused.length} tulajdonságra (${names.join(", ")}${more}) nem tud szűrni a motor: a nevükben idézőjel, visszaper vagy vezérlőkarakter van, vagy 120 karakternél hosszabbak. Szólj, ha ezek közül kell valamelyik.`,
  };
}

// --- The attribute key ------------------------------------------------------

function AttributeKey({
  value,
  env,
  disabled,
  onChange,
}: {
  value: string;
  env: BuilderEnv;
  disabled?: boolean;
  onChange: (key: string) => void;
}) {
  const listId = useId();
  const usable = env.attributes?.keys.filter((key) => key.filterable) ?? [];
  return (
    <>
      <Input
        aria-label="Tulajdonság neve"
        className="w-40"
        placeholder="kulcs, pl. gender"
        list={listId}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <datalist id={listId}>
        {usable.map((key) => (
          <option key={key.key} value={key.key}>
            {`${key.count.toLocaleString("hu-HU")} kontaktnál`}
          </option>
        ))}
      </datalist>
    </>
  );
}

// --- Values -------------------------------------------------------------------

function knownField(condition: ConditionDraft, info: FieldInfo): string | null {
  if (info.kind === "text") return condition.subject;
  if (info.kind === "attribute" && attributeKeyOk(condition.attrKey)) return `attr.${condition.attrKey}`;
  return null;
}

function ValueEditor({
  condition,
  info,
  env,
  disabled,
  onChange,
}: {
  condition: ConditionDraft;
  info: FieldInfo;
  env: BuilderEnv;
  disabled?: boolean;
  onChange: (next: ConditionDraft) => void;
}) {
  const op = condition.op;
  const set = (patch: Partial<ConditionDraft>) => onChange({ ...condition, ...patch });

  if (info.kind === "time") {
    if (op === "within_days" || op === "not_within_days") {
      return (
        <span className="inline-flex items-center gap-2">
          <Input
            aria-label="Napok száma"
            className="w-24"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_DAYS}
            value={condition.text}
            disabled={disabled}
            onChange={(event) => set({ text: event.target.value })}
          />
          <span className="text-muted-foreground">nap</span>
        </span>
      );
    }
    if (op === BETWEEN_DATES) {
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          <Input
            aria-label="Első nap"
            className="w-40"
            type="date"
            value={condition.low}
            disabled={disabled}
            onChange={(event) => set({ low: event.target.value })}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            aria-label="Utolsó nap"
            className="w-40"
            type="date"
            value={condition.high}
            disabled={disabled}
            onChange={(event) => set({ high: event.target.value })}
          />
          <span className="text-label text-muted-foreground">mindkét nap beleszámít</span>
        </span>
      );
    }
    // A stored full timestamp stays editable as written; a date picker
    // would silently drop its time of day.
    const plainDay = condition.text === "" || /^\d{4}-\d{2}-\d{2}$/.test(condition.text);
    return (
      <Input
        aria-label="Nap"
        className={plainDay ? "w-40" : "w-64"}
        type={plainDay ? "date" : "text"}
        value={condition.text}
        disabled={disabled}
        onChange={(event) => set({ text: event.target.value })}
      />
    );
  }

  if (info.kind === "int" && info.bounds) {
    // A handful of values (the seven waves): picked, not typed.
    const [low, high] = info.bounds;
    const values = Array.from({ length: high - low + 1 }, (_, index) => String(low + index));
    const pick = (label: string, value: string, patch: (value: string) => Partial<ConditionDraft>) => (
      <NativeSelect
        aria-label={label}
        className="w-24"
        value={value}
        disabled={disabled}
        onChange={(event) => set(patch(event.target.value))}
      >
        {value === "" && <option value="">…</option>}
        {values.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </NativeSelect>
    );
    if (op === "between") {
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          {pick("Legalább", condition.low, (next) => ({ low: next }))}
          <span className="text-muted-foreground">és</span>
          {pick("Legfeljebb", condition.high, (next) => ({ high: next }))}
        </span>
      );
    }
    return pick("Érték", condition.text, (next) => ({ text: next }));
  }

  if (info.kind === "int") {
    const money = subjectInfo(condition.subject)?.money ?? false;
    const numberInput = (label: string, value: string, patch: (value: string) => Partial<ConditionDraft>) => (
      <span className="inline-flex items-center gap-1.5">
        <Input
          aria-label={label}
          className="w-28"
          type={money ? "text" : "number"}
          inputMode={money ? "decimal" : "numeric"}
          placeholder={money ? "0,00" : "0"}
          value={value}
          disabled={disabled}
          onChange={(event) => set(patch(event.target.value))}
        />
        {money && <span className="text-muted-foreground">€</span>}
      </span>
    );
    if (op === "between") {
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          {numberInput("Legalább", condition.low, (low) => ({ low }))}
          <span className="text-muted-foreground">és</span>
          {numberInput("Legfeljebb", condition.high, (high) => ({ high }))}
        </span>
      );
    }
    return numberInput("Érték", condition.text, (text) => ({ text }));
  }

  if (info.kind === "enum") {
    if (LISTED.has(op)) {
      return (
        <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
          {info.choices.map((choice) => (
            <label key={choice} className="inline-flex cursor-pointer items-center gap-1.5">
              <Checkbox
                checked={condition.list.includes(choice)}
                disabled={disabled}
                onChange={(event) =>
                  set({
                    list: event.target.checked
                      ? [...condition.list, choice]
                      : condition.list.filter((item) => item !== choice),
                  })
                }
              />
              {CONSENT_WORDS[choice] ?? choice}
            </label>
          ))}
        </span>
      );
    }
    return (
      <NativeSelect
        aria-label="Érték"
        className="min-w-48"
        value={condition.text}
        disabled={disabled}
        onChange={(event) => set({ text: event.target.value })}
      >
        <option value="">Válassz…</option>
        {info.choices.map((choice) => (
          <option key={choice} value={choice}>
            {CONSENT_WORDS[choice] ?? choice}
          </option>
        ))}
      </NativeSelect>
    );
  }

  if (info.kind === "membership") {
    const options = condition.subject === "segment" ? env.segments : env.lists;
    return (
      <MembershipPicker
        noun={condition.subject === "segment" ? "szegmens" : "lista"}
        options={options}
        chosen={condition.list}
        disabled={disabled}
        onChange={(list) => set({ list })}
      />
    );
  }

  // Text and attribute values, with the country's frequent values offered.
  const field = knownField(condition, info);
  const known = field ? env.values.get(field) : undefined;
  const suggestions = typeof known === "object" ? known.values : [];
  const sampled = typeof known === "object" && !known.complete;
  const load = () => field && env.values.load(field);

  if (LISTED.has(op)) {
    return (
      <ValuesInput
        values={condition.list}
        suggestions={suggestions}
        sampled={sampled}
        disabled={disabled}
        onFocus={load}
        onChange={(list) => set({ list })}
      />
    );
  }
  return (
    <SuggestedInput
      value={condition.text}
      suggestions={suggestions}
      sampled={sampled}
      disabled={disabled}
      placeholder={op === "starts_with" ? "kezdőszöveg" : "érték"}
      onFocus={load}
      onChange={(text) => set({ text })}
    />
  );
}

type Suggestion = { value: string; count: number };

function suggestionText(item: Suggestion, sampled: boolean): string {
  return `${item.count.toLocaleString("hu-HU")} kontakt${sampled ? " a mintában" : ""}`;
}

function SuggestedInput({
  value,
  suggestions,
  sampled,
  disabled,
  placeholder,
  onFocus,
  onChange,
}: {
  value: string;
  suggestions: Suggestion[];
  sampled: boolean;
  disabled?: boolean;
  placeholder: string;
  onFocus: () => void;
  onChange: (value: string) => void;
}) {
  const listId = useId();
  return (
    <>
      <Input
        aria-label="Érték"
        className="w-56"
        list={listId}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onChange={(event) => onChange(event.target.value)}
      />
      <datalist id={listId}>
        {suggestions.map((item) => (
          <option key={item.value} value={item.value}>
            {suggestionText(item, sampled)}
          </option>
        ))}
      </datalist>
    </>
  );
}

/** Several values as chips: Enter or a comma adds, a pasted list is split,
 * Backspace on an empty field takes the last one back. */
function ValuesInput({
  values,
  suggestions,
  sampled,
  disabled,
  onFocus,
  onChange,
}: {
  values: string[];
  suggestions: Suggestion[];
  sampled: boolean;
  disabled?: boolean;
  onFocus: () => void;
  onChange: (values: string[]) => void;
}) {
  const [typed, setTyped] = useState("");
  const listId = useId();

  function add(raw: string[]) {
    const fresh = raw.map((item) => item.trim()).filter((item) => item !== "" && !values.includes(item));
    if (fresh.length > 0) onChange([...values, ...new Set(fresh)]);
    setTyped("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add([typed]);
    } else if (event.key === "Backspace" && typed === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!/[,;\n\t]/.test(text)) return;
    event.preventDefault();
    add(text.split(/[,;\n\t]/));
  }

  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
      {values.map((value) => (
        <span
          key={value}
          className="inline-flex h-7 max-w-64 items-center gap-1 rounded-control border border-line bg-surface-2 pr-1 pl-2"
        >
          <span className="truncate">{value}</span>
          <button
            type="button"
            className="inline-flex size-5 cursor-pointer items-center justify-center rounded-control text-muted-foreground hover:bg-line hover:text-ink"
            aria-label={`${value} eltávolítása`}
            disabled={disabled}
            onClick={() => onChange(values.filter((item) => item !== value))}
          >
            <XIcon className="size-3.5" />
          </button>
        </span>
      ))}
      <Input
        aria-label="Érték hozzáadása"
        className="w-48"
        list={listId}
        placeholder={values.length === 0 ? "érték, majd Enter" : "még egy…"}
        value={typed}
        disabled={disabled}
        onFocus={onFocus}
        onChange={(event) => {
          const next = event.target.value;
          // Picking from the suggestion list fills the field in one go.
          if (suggestions.some((item) => item.value === next)) add([next]);
          else setTyped(next);
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => typed.trim() && add([typed])}
      />
      <datalist id={listId}>
        {suggestions
          .filter((item) => !values.includes(item.value))
          .map((item) => (
            <option key={item.value} value={item.value}>
              {suggestionText(item, sampled)}
            </option>
          ))}
      </datalist>
    </span>
  );
}

function MembershipPicker({
  noun,
  options,
  chosen,
  disabled,
  onChange,
}: {
  noun: string;
  options: PickerOption[];
  chosen: string[];
  disabled?: boolean;
  onChange: (ids: string[]) => void;
}) {
  const byId = new Map(options.map((option) => [String(option.id), option]));
  const left = options.filter((option) => !chosen.includes(String(option.id)));
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
      {chosen.map((id) => (
        <span
          key={id}
          className="inline-flex h-7 max-w-64 items-center gap-1 rounded-control border border-line bg-surface-2 pr-1 pl-2"
        >
          <span className="truncate">{byId.get(id)?.name ?? `#${id}`}</span>
          <button
            type="button"
            className="inline-flex size-5 cursor-pointer items-center justify-center rounded-control text-muted-foreground hover:bg-line hover:text-ink"
            aria-label={`${byId.get(id)?.name ?? id} eltávolítása`}
            disabled={disabled}
            onClick={() => onChange(chosen.filter((item) => item !== id))}
          >
            <XIcon className="size-3.5" />
          </button>
        </span>
      ))}
      <NativeSelect
        aria-label={`${noun} hozzáadása`}
        className="min-w-48"
        value=""
        disabled={disabled || left.every((option) => option.disabled)}
        onChange={(event) => event.target.value && onChange([...chosen, event.target.value])}
      >
        <option value="">
          {left.length === 0
            ? options.length === 0
              ? `Nincs ${noun} ebben az országban`
              : "Mind kiválasztva"
            : `+ ${noun} hozzáadása`}
        </option>
        {left.map((option) => (
          <option key={option.id} value={option.id} disabled={option.disabled !== undefined}>
            {option.disabled ? `${option.name} — ${option.disabled}` : option.name}
          </option>
        ))}
      </NativeSelect>
    </span>
  );
}
